// ProspectLeadsDashboard.jsx
import React, { useState, useEffect, useCallback, useRef } from 'react';
import ApiClient from '../auth/AuthService';
import {
  Container,
  Paper,
  Typography,
  TextField,
  Button,
  Box,
  Select,
  FormControl,
  InputLabel,
  CircularProgress,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import { Search, Refresh, PlayArrow, SmartToy } from '@mui/icons-material';
import LeadInfoDialog from '../LeadInfo/LeadInfoDialog';
import { GridToolbarContainer, GridToolbarExport } from '@mui/x-data-grid';


const ProspectLeadsDashboard = ({ user }) => {
  const [apiClient] = useState(ApiClient());
  const today = new Date().toISOString().split('T')[0];
  const [startDate, setStartDate] = useState(localStorage.getItem('startDate') || today);
  const [endDate, setEndDate] = useState(localStorage.getItem('endDate') || today);
  const [leads, setLeads] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [action, setAction] = useState('');
  const [selectedLeads, setSelectedLeads] = useState([]);
  const [selectedLead, setSelectedLead] = useState(null);
  const [isLeadInfoOpen, setIsLeadInfoOpen] = useState(false);

  // Reply with AI: запуск ai_answer.py на сервере и вывод его лога в модалке
  const [aiDialogOpen, setAiDialogOpen] = useState(false);
  const [aiLines, setAiLines] = useState([]);
  const [aiStatus, setAiStatus] = useState('running'); // running | done | error
  const [aiError, setAiError] = useState(null);
  const [aiRunMessage, setAiRunMessage] = useState(null);
  const [aiStarting, setAiStarting] = useState(false);
  const aiOffsetRef = useRef(0);
  const aiIntervalRef = useRef(null);
  const aiPollingRef = useRef(false);
  const aiLogRef = useRef(null);


  const fetchLeads = useCallback(async (start_date, end_date) => {
    setIsLoading(true);
    setError(null);
    try {
      const response = await apiClient.get('/api/prospect/', {
        params: { start_date, end_date },
      });
      console.log("API response:", response);
      if (Array.isArray(response.leads)) {
        const formattedLeads = response.leads.map(lead => ({
          ...lead,
          id: lead.id_lead,
          created_at: lead.created_at ? new Date(lead.created_at).toISOString() : '',
        }));
        setLeads(formattedLeads);
                } else {
            throw new Error('Received unexpected data format from server.');
          }
        } catch (err) {
          console.error("Error fetching leads:", err);
          setError(err.message || 'Failed to fetch leads. Please try again.');
        } finally {
          setIsLoading(false);
        }
      }, [apiClient]);


  useEffect(() => {
    fetchLeads(today, today);
  }, [fetchLeads, today]);



  const handleRowDoubleClick = async (params) => {
  const selectedRow = params.row;
  setSelectedLead(selectedRow);
  setIsLeadInfoOpen(true);
  };

  const handleStartDateChange = (e) => {
    const date = e.target.value;
    setStartDate(date);
    localStorage.setItem('startDate', date);
  };

  const handleEndDateChange = (e) => {
    const date = e.target.value;
    setEndDate(date);
    localStorage.setItem('endDate', date);
  };

  useEffect(() => {
    const handleBeforeUnload = () => {
      localStorage.removeItem('startDate');
      localStorage.removeItem('endDate');
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, []);

  const handleSearch = () => {
    console.log("Searching with dates:", startDate, endDate);
    fetchLeads(startDate, endDate);
  };

  const handleReset = () => {
    setStartDate(today);
    setEndDate(today);
    fetchLeads(today, today);
  };

  const stopAiPolling = useCallback(() => {
    if (aiIntervalRef.current) {
      clearInterval(aiIntervalRef.current);
      aiIntervalRef.current = null;
    }
  }, []);

  const pollAiStatus = useCallback(async () => {
    // Не запускаем новый запрос, пока не завершился предыдущий
    if (aiPollingRef.current) return;
    aiPollingRef.current = true;
    try {
      const response = await apiClient.get('/api/ai-answer/status/', { offset: aiOffsetRef.current });
      if (!aiIntervalRef.current) return; // модалку уже закрыли
      aiOffsetRef.current = response.offset;
      if (response.lines && response.lines.length > 0) {
        setAiLines((prev) => [...prev, ...response.lines]);
      }
      if (response.status !== 'running') {
        stopAiPolling();
        setAiStatus('done');
      }
    } catch (err) {
      if (!aiIntervalRef.current) return;
      stopAiPolling();
      const httpStatus = err.response ? `HTTP ${err.response.status}: ` : '';
      setAiError(`${httpStatus}${err.message || 'Network error'}`);
      setAiStatus('error');
    } finally {
      aiPollingRef.current = false;
    }
  }, [apiClient, stopAiPolling]);

  const handleReplyWithAi = async () => {
    setAiRunMessage(null);
    setAiStarting(true);
    try {
      await apiClient.post('/api/ai-answer/run/', {});
      aiOffsetRef.current = 0;
      setAiLines([]);
      setAiError(null);
      setAiStatus('running');
      setAiDialogOpen(true);
      stopAiPolling();
      aiIntervalRef.current = setInterval(pollAiStatus, 2000);
      pollAiStatus();
    } catch (err) {
      if (err.response && err.response.status === 409) {
        setAiRunMessage('AI reply is already running');
      } else {
        const httpStatus = err.response ? `HTTP ${err.response.status}: ` : '';
        setAiRunMessage(`Failed to start AI reply. ${httpStatus}${err.message || ''}`);
      }
    } finally {
      setAiStarting(false);
    }
  };

  const handleCloseAiDialog = () => {
    stopAiPolling();
    setAiDialogOpen(false);
  };

  // Интервал очищается при размонтировании компонента
  useEffect(() => stopAiPolling, [stopAiPolling]);

  // Автоскролл вниз при новых строках
  useEffect(() => {
    if (aiLogRef.current) {
      aiLogRef.current.scrollTop = aiLogRef.current.scrollHeight;
    }
  }, [aiLines, aiStatus]);

  const updateLeadStatus = (leadId, newStatus, newStatusId) => {
    console.log('&&&&&&&&&& newStatus', newStatus)
    setLeads((prevLeads) =>
      prevLeads.map((lead) =>
        lead.id_lead === leadId ? { ...lead, status: newStatus, id_status: newStatusId } : lead
      )
    );
  };

  const handleAction = async () => {
    try {
      setIsLoading(true);
      await apiClient.post('/api/update_leads_status/', {
        start_date: startDate,
        end_date: endDate,
        action,
        selected_leads: selectedLeads,  username: user.username,
      });
      await fetchLeads(startDate, endDate);
      setSelectedLeads([]);
      setAction('');
    } catch (err) {
      if (err.response && err.response.status === 401) {
        setError(err.message || 'Failed to perform action. Please try again.');
      } else {
        // Обработка других ошибок
        setError('An unexpected error occurred. Please try again.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'No Date Provided';
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return 'Invalid Date';
    return new Intl.DateTimeFormat('en-US', {
      month: '2-digit',
      day: '2-digit',
      year: 'numeric'
    }).format(date);
  };

  const columns = [
    { field: 'id', headerName: 'ID', width: 90, hide: true },
    { field: 'phone', headerName: 'Phone', width: 130 },
    { field: 'client_name', headerName: 'Client Name', width: 200 },
    { field: 'ai_classification', headerName: 'AI classification', width: 150 },
    { field: 'communication_history', headerName: 'Communication History', width: 300 },
    { field: 'location_zone', headerName: 'Location Zone', width: 150 },
    { field: 'created_at', headerName: 'Created At', width: 200, renderCell: (params) => formatDate(params.value),},
    { field: 'from_number', headerName: 'SMS to Twilio', width: 150 },
    { field: 'fl_ai_answer', headerName: 'Flag answer', width: 100 },
  ];

  const CustomToolbar = () => {
    return (
      <GridToolbarContainer>
        <GridToolbarExport
          csvOptions={{
            utf8WithBom: true,
            fileName: 'Leads_export.csv'
          }}
        />
      </GridToolbarContainer>
    );
  };


  return (
    <>
     <Box mt={4}>
      <Container>
        <Paper>
          <Box sx={{ padding: 2 }}>
            <Typography variant="h5">Prospect Leads Dashboard</Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', marginTop: 2 }}>
              <TextField
                label="Start Date"
                type="date"
                value={startDate}
                onChange={handleStartDateChange}
                sx={{ marginRight: 2 }}
                InputLabelProps={{
                  shrink: true,
                }}
              />
              <TextField
                label="End Date"
                type="date"
                value={endDate}
                onChange={handleEndDateChange}
                sx={{ marginRight: 2 }}
                InputLabelProps={{
                  shrink: true,
                }}
              />
              <Button
                variant="contained"
                color="primary"
                startIcon={<Search />}
                onClick={handleSearch}
                sx={{ marginRight: 2 }}
              >
                Search
              </Button>
              <Button
                variant="outlined"
                startIcon={<Refresh />}
                onClick={handleReset}
                sx={{ marginRight: 2 }}
              >
                Reset
              </Button>
              <Button
                variant="contained"
                color="primary"
                startIcon={<SmartToy />}
                onClick={handleReplyWithAi}
                disabled={aiStarting}
                sx={{ marginLeft: 'auto' }}
              >
                Reply with AI
              </Button>
            </Box>
            {aiRunMessage && (
              <Typography variant="body2" color="error" sx={{ marginTop: 1, textAlign: 'right' }}>
                {aiRunMessage}
              </Typography>
            )}
            <Box sx={{ marginTop: 2 }}>
              <FormControl sx={{ minWidth: 200, marginRight: 2 }}>
                <InputLabel>Action</InputLabel>
                <Select
                  value={action}
                  onChange={(e) => setAction(e.target.value)}
                  label="Action"
                >
                  <MenuItem value="transfer_lead">Transfer to Lead status</MenuItem>
                  <MenuItem value="make_inactive">Make records inactive</MenuItem>
                </Select>
              </FormControl>
              <Button
                variant="contained"
                color="secondary"
                startIcon={<PlayArrow />}
                onClick={handleAction}
                disabled={!action || selectedLeads.length === 0}
                sx={{ marginTop: 1 }}
              >
                Apply
              </Button>
            </Box>
            {isLoading && <CircularProgress sx={{ marginTop: 2 }} />}
            {!isLoading && leads.length === 0 && (
              <Typography variant="body1" sx={{ marginTop: 2 }}>
                No leads found for the selected date range.
              </Typography>
            )}
            {!isLoading && leads.length > 0 && (
              <DataGrid
                rows={leads}
                columns={columns}
                pageSize={5}
                rowsPerPageOptions={[5]}
                checkboxSelection
                disableSelectionOnClick
                components={{Toolbar: CustomToolbar}}
                onRowSelectionModelChange={(newSelectionModel) => {
                setSelectedLeads(newSelectionModel);
                }}
                keepNonExistentRowsSelected
                onRowDoubleClick={handleRowDoubleClick}

                getRowClassName={(params) => {
                    if (params.row.fl_ai_answer === true) {
                      return 'highlight-row-blue';
                    } else if (params.row.ai_classification === 'potential_lead_message') {
                      return 'highlight-row';
                    } else {
                      return '';
                    }
                  }}

                  sx={{
                    marginTop: 2,
                    '& .highlight-row': {
                      backgroundColor: 'rgba(144, 238, 144, 0.3)'
                    },
                    '& .highlight-row-blue': {
                      backgroundColor: 'rgba(211, 211, 211, 0.5)'  // светло-серый цвет
                    }
                  }}


              />
            )}
            {error && (
              <Typography variant="body1" color="error" sx={{ marginTop: 2 }}>
                {error}
              </Typography>
            )}

            {selectedLead && (
              <LeadInfoDialog
                open={isLeadInfoOpen}
                onClose={() => {
                  setIsLeadInfoOpen(false);
                  setSelectedLead(null);
                }}
                leadData={selectedLead}
                apiClient={apiClient}
                cUser={user}
                updateLeadStatus={updateLeadStatus}
              />
            )}
          </Box>
        </Paper>
      </Container>
     </Box>

     <Dialog
       open={aiDialogOpen}
       onClose={() => {
         // Пока скрипт работает, закрыть можно только кнопкой Close (она неактивна)
         if (aiStatus !== 'running') handleCloseAiDialog();
       }}
       maxWidth="md"
       fullWidth
     >
       <DialogTitle>Reply with AI</DialogTitle>
       <DialogContent>
         <Box
           ref={aiLogRef}
           sx={{
             fontFamily: 'monospace',
             fontSize: 13,
             whiteSpace: 'pre-wrap',
             wordBreak: 'break-word',
             backgroundColor: '#f5f5f5',
             border: '1px solid #ddd',
             borderRadius: 1,
             padding: 1.5,
             height: 400,
             overflowY: 'auto',
           }}
         >
           {aiLines.join('\n')}
         </Box>
         <Box sx={{ display: 'flex', alignItems: 'center', marginTop: 2 }}>
           {aiStatus === 'running' && (
             <>
               <CircularProgress size={20} sx={{ marginRight: 1 }} />
               <Typography variant="body2">Running…</Typography>
             </>
           )}
           {aiStatus === 'done' && (
             <Typography variant="body2" sx={{ color: 'success.main' }}>Finished</Typography>
           )}
           {aiStatus === 'error' && (
             <Typography variant="body2" color="error" sx={{ whiteSpace: 'pre-wrap' }}>
               {aiError}
             </Typography>
           )}
         </Box>
       </DialogContent>
       <DialogActions>
         <Button onClick={handleCloseAiDialog} disabled={aiStatus === 'running'}>
           Close
         </Button>
       </DialogActions>
     </Dialog>
    </>
  );
};

export default ProspectLeadsDashboard;